<template>
  <div class="no-padding">
    <label v-if="editable" class="editable-header" @click="$emit('edit_clicked', 'header', 0)" v-html="header_html"></label>
    <label v-else class="question-label">{{ item.header || item.name }}</label>

    <div class="stars-options">
      <!-- Create stars based on num_options (1-5 stars) -->
      <span 
        v-for="(star, index) in 5" 
        :key="index" 
        class="star-icon"
        @click="toggleSelection(index + 1)"
        @mouseover="hoverStar(index + 1)"
        @mouseleave="resetHover"
      >
        <!-- Show filled star if selected or hovered, otherwise empty star -->
        <svg v-if="selected >= index + 1 || hover >= index + 1" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M11.5245 4.46353C11.6741 4.00287 12.3259 4.00287 12.4755 4.46353L13.9084 8.87336C13.9753 9.07937 14.1673 9.21885 14.3839 9.21885H19.0207C19.505 9.21885 19.7064 9.83866 19.3146 10.1234L15.5633 12.8488C15.3881 12.9761 15.3148 13.2018 15.3817 13.4078L16.8145 17.8176C16.9642 18.2783 16.437 18.6613 16.0451 18.3766L12.2939 15.6512C12.1186 15.5239 11.8814 15.5239 11.7061 15.6512L7.95488 18.3766C7.56303 18.6613 7.03578 18.2783 7.18546 17.8176L8.6183 13.4078C8.68524 13.2018 8.61191 12.9761 8.43667 12.8488L4.68544 10.1234C4.29358 9.83866 4.49497 9.21885 4.97933 9.21885H9.6161C9.83272 9.21885 10.0247 9.07937 10.0916 8.87336L11.5245 4.46353Z" fill="#FFD700" stroke="#888888" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        <svg v-else viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M11.5245 4.46353C11.6741 4.00287 12.3259 4.00287 12.4755 4.46353L13.9084 8.87336C13.9753 9.07937 14.1673 9.21885 14.3839 9.21885H19.0207C19.505 9.21885 19.7064 9.83866 19.3146 10.1234L15.5633 12.8488C15.3881 12.9761 15.3148 13.2018 15.3817 13.4078L16.8145 17.8176C16.9642 18.2783 16.437 18.6613 16.0451 18.3766L12.2939 15.6512C12.1186 15.5239 11.8814 15.5239 11.7061 15.6512L7.95488 18.3766C7.56303 18.6613 7.03578 18.2783 7.18546 17.8176L8.6183 13.4078C8.68524 13.2018 8.61191 12.9761 8.43667 12.8488L4.68544 10.1234C4.29358 9.83866 4.49497 9.21885 4.97933 9.21885H9.6161C9.83272 9.21885 10.0247 9.07937 10.0916 8.87336L11.5245 4.46353Z" stroke="#cccccc" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </span>
    </div>

    <!-- Conditional text input based on selected option -->
    <div v-if="show_text">
      <label for="input-text">Please provide additional details:</label>
      <input 
        v-model="input_text" 
        type="text" 
        id="input-text" 
        class="text-input"
        placeholder="Enter text"
      />
    </div>
  </div>
</template>

<script>
export default {
  name: 'QuestionStarsOption',
  props: ['item', 'editable'],
  emits: ['option_update', 'selected', 'edit_clicked'],
  data() {
    return {
      selected: 0, // Track selected star (1-5)
      hover: 0, // Track hovered star
      show_text: false,
      input_text: null,
    };
  },
  computed: {
    header_html() {
      return this.item.header || this.item.name;
    }
  },
  watch: {
    item: {
      deep: true,
      handler() {
        this.selected = 0;
      }
    },
    input_text: {
      deep: true,
      handler() {
        if (this.input_text) {
          this.$emit('selected', this.selected, this.input_text);
        }
      }
    },
    selected: {
      deep: true,
      handler() {
        this.$emit('selected', this.selected, this.input_text);
        const unlock_text = this.item.options.find(i => i.unlock_text);
        this.show_text = !!unlock_text && this.selected > 0;
      }
    }
  },
  methods: {
    option_edit_clicked(part, index) {
      this.$emit('edit_clicked', 'question_text', index);
    },
    text_updated(option, value) {
      this.$emit('option_update', option, value);
    },
    toggleSelection(value) {
      // Toggle the star selection
      if (this.selected === value) {
        this.selected = 0; // Clear selection if same star is clicked again
      } else {
        this.selected = value; // Select the clicked star
      }
    },
    hoverStar(value) {
      this.hover = value; // Set the hovered star
    },
    resetHover() {
      this.hover = 0; // Reset hover on mouseleave
    }
  }
};
</script>

<style lang="scss" scoped>
.question-label {
  display: block;
  font-weight: bold;
  margin-bottom: 5px;
}

.stars-options {
  display: flex;
  justify-content: space-between; /* Ensure the stars span the entire width */
  width: 100%;
}

.star-icon {
  display: inline-block;
  font-size: 3em; /* Size of the star */
  cursor: pointer;
  transition: color 0.3s ease;
  padding: 0.2em;
}

.star-icon svg {
  width: 100%;
  height: 100%;
}

.star-icon:hover svg {
  fill: #FFD700; /* Gold color on hover */
}

.text-input {
  padding: 8px;
  width: 100%;
  margin-top: 10px;
}

.editable-header {
  min-width: 300px;
  min-height: 20px;
  padding: 8px;
}

.editable-header:hover {
  border: 2px solid rgb(50, 201, 50);
}
</style>
